import dayjs, { Dayjs } from 'dayjs';

type OptionalString = string | null;

type DateTimeParams = {
  datetime?: OptionalString;
  date?: OptionalString;
  time?: OptionalString;
  timezoneId?: OptionalString;
};

type DateTimeTemplate = 'll' | 'YYYY-MM-DD' | 'HH:mm' | 'LT';

const getDstOffset = (dateTime: dayjs.Dayjs, timeZoneId: string) =>
  Math.abs(
    dateTime.utcOffset() -
      Math.min(
        dayjs.tz('2022-01-01', timeZoneId).utcOffset(),
        dayjs.tz('2022-06-01', timeZoneId).utcOffset(),
      ),
  );

const convertLocalToUTC = (localDateTime: dayjs.Dayjs, timeZoneId: string) => {
  const dstOffset = getDstOffset(localDateTime, timeZoneId);

  return dstOffset ? localDateTime.add(dstOffset, 'minute').utc() : localDateTime.utc();
};

const utils = (djs?: Dayjs, timezoneId?: OptionalString) => ({
  dayjs: () => djs,
  toDate: () =>
    djs && timezoneId
      ? new Date(djs?.toDate().toLocaleString('en-US', { timeZone: timezoneId }))
      : undefined,
  format: (f: DateTimeTemplate, defaultValue?: string) => djs?.format(f) ?? defaultValue,
});

export const timeToDayJs = (time: string) => dayjs.utc(time, 'HH:mm');

export const datetimeWithTimezone = (params: DateTimeParams) => {
  const { datetime, date, time, timezoneId } = params;

  const normalisedDateTime = datetime ?? (date && time ? `${date} ${time}` : null);

  const dt =
    normalisedDateTime && dayjs(normalisedDateTime).isValid() ? normalisedDateTime : undefined;

  return {
    utc: () => {
      const djs = dt && timezoneId ? dayjs.tz(dt, timezoneId).utc() : undefined;

      return utils(djs, timezoneId);
    },
    tz: () => {
      const djs = dt && timezoneId ? dayjs.utc(dt).tz(timezoneId) : undefined;

      return utils(djs, timezoneId);
    },
  };
};

const getDateTimeFromTime = (time: string) => dayjs().format(`YYYY-MM-DD ${time.slice(0, 5)}`);

const convertUTCToLocal = (
  utcDateTime: dayjs.Dayjs,
  timeZoneId: string,
  useStandardTime: boolean,
) => {
  const localDateTime = utcDateTime.tz(timeZoneId);
  const dstOffset = getDstOffset(localDateTime, timeZoneId);

  return useStandardTime && dstOffset ? localDateTime.subtract(dstOffset, 'minute') : localDateTime;
};

export const getTimeInTimezone = (
  time?: string,
  timeZoneId?: Nullable<string>,
  useStandardTime = true,
): string | undefined =>
  time && timeZoneId && dayjs(getDateTimeFromTime(time)).isValid()
    ? convertUTCToLocal(dayjs.utc(getDateTimeFromTime(time)), timeZoneId, useStandardTime).format(
        'HH:mm',
      )
    : undefined;

export const toUtcTime = (time: string, timezoneId?: string): string =>
  datetimeWithTimezone({
    datetime: timeToDayJs(time).format('YYYY-MM-DD HH:mm'),
    timezoneId,
  })
    .utc()
    .format('HH:mm') ?? '';

export const getUTCTimeFromTimeInTimezone = (
  time?: string,
  timeZoneId?: Nullable<string>,
): string =>
  time && timeZoneId && dayjs(getDateTimeFromTime(time)).isValid()
    ? convertLocalToUTC(dayjs.tz(getDateTimeFromTime(time), timeZoneId), timeZoneId).format('HH:mm')
    : '00:00';

export const getTimeInTimezoneInDayjs = (
  time?: string,
  timeZoneId?: Nullable<string>,
  useStandardTime = true,
): Dayjs | undefined =>
  time && timeZoneId && dayjs(getDateTimeFromTime(time)).isValid()
    ? convertUTCToLocal(dayjs.utc(getDateTimeFromTime(time)), timeZoneId, useStandardTime)
    : undefined;

export const getDateAndTimeInTimezoneInDayjs = (
  date?: string,
  timeZoneId?: Nullable<string>,
  useStandardTime = true,
): Dayjs | undefined =>
  date && timeZoneId && dayjs(date).isValid()
    ? convertUTCToLocal(dayjs.utc(date), timeZoneId, useStandardTime)
    : undefined;
